import React, { useCallback, useReducer, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { Checkbox } from 'ui-components/components/checkbox';
import { CurrencyInput } from 'ui-components/components/currency-input';
import { DimensionInput } from 'ui-components/components/dimension-input';
import { Input } from 'ui-components/components/input';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { WeightInput } from 'ui-components/components/weight-input';
import { noop } from 'ui-components/default-props/noop';

import * as constants from './constants';
import { editModalHeader, addModalHeader, save, cancel } from './formattedMessageConstants';
import messages from './messages';
import { presetGroupReducer, initialState } from './modal/preset-group-reducer';
import PresetGroupsShippingServiceDropdowns from './shipping-service-dropdowns';
import style from './style.module.less';
import { CountryDropdown } from '../../../components/customs-declarations/components/origin-country-dropdown/origin-country-dropdown';
import AsModal from '../../../components/as-modal';
import { SellerSettings } from '../../../enums/seller-settings/seller-settings';
import { DimensionUnit } from '../../../enums/dimension-unit';
import { WeightUnit } from '../../../enums/weight-unit';
import { useProductPresetsSetupCompleteSellerSetting } from '../../../hooks/use-seller-setting';
import { updateSellerSettingsBulk } from '../../settings/api/save-settings';
import { LogService } from '../../../services/log-service';
import { entitiesLoad } from '../../../store/entities/entities.actions';
import Global from '../../../store/global-selectors';
import { saveNewSellerSettings } from '../../../store/seller/seller.actions';
import { updatePresetGroup, addPresetGroup } from '../../../store/products/products.api';

const PresetGroupModal = ({ presetGroupId = '', onClose, needsReload = false, setNeedsReload = noop, newPresetGroup = false }) => {
  const dispatch = useDispatch();
  // oxlint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const presetGroupData = useSelector((state) => Global.selectEntitiesPresetGroupById(state, { profileId: presetGroupId })) || {};
  const sellerDimUnit = useSelector(Global.selectSellerDefaultDimensionUnit);
  const sellerWeightUnit = useSelector(Global.selectSellerDefaultWeightUnit);
  const sellerDefaults = { sellerDimUnit, sellerWeightUnit };
  const currencyCode = useSelector(Global.selectSellerDefaultCurrency);
  const isProductPresetsSetupComplete = useProductPresetsSetupCompleteSellerSetting();

  const [state, dispatchLocal] = useReducer(presetGroupReducer, initialState(presetGroupData, sellerDefaults));
  const onChangeConfirmationId = useCallback(
    (value) =>
      dispatchLocal({
        type: 'set',
        fieldName: constants.confirmationId,
        payload: value,
      }),
    [],
  );
  const onChangePackageTypeId = useCallback(
    (value) =>
      dispatchLocal({
        type: 'set',
        fieldName: constants.packageTypeId,
        payload: value,
      }),
    [],
  );
  const onChangeIntlConfirmationId = useCallback(
    (value) =>
      dispatchLocal({
        type: 'set',
        fieldName: constants.intlConfirmationId,
        payload: value,
      }),
    [],
  );
  const onChangeIntlPackageTypeId = useCallback(
    (value) =>
      dispatchLocal({
        type: 'set',
        fieldName: constants.intlPackageTypeId,
        payload: value,
      }),
    [],
  );
  const onChangeName = useCallback((value) => dispatchLocal({ type: 'set', fieldName: constants.name, payload: value }), []);
  const onChangeWeight = useCallback(
    (value) =>
      dispatchLocal({
        type: 'set',
        fieldName: constants.weight,
        payload: value,
      }),
    [],
  );
  const onChangeNoCustoms = useCallback(
    (value) =>
      dispatchLocal({
        type: 'set',
        fieldName: constants.noCustoms,
        payload: value,
      }),
    [],
  );
  const onChangeCustomsDescription = useCallback(
    (value) =>
      dispatchLocal({
        type: 'set',
        fieldName: constants.customsDescription,
        payload: value,
      }),
    [],
  );
  const onChangeCustomsValue = useCallback(
    (value) =>
      dispatchLocal({
        type: 'set',
        fieldName: constants.customsValue,
        payload: value,
      }),
    [],
  );
  const onChangeCustomsHarmonizationCode = useCallback(
    (value) =>
      dispatchLocal({
        type: 'set',
        fieldName: constants.customsHarmonizationCode,
        payload: value,
      }),
    [],
  );
  const onChangeCustomsCountry = useCallback(
    (value) =>
      dispatchLocal({
        type: 'set',
        fieldName: constants.customsCountry,
        payload: value,
      }),
    [],
  );
  const onChangeBulkHandler = useCallback((value) => dispatchLocal({ type: 'setBulk', payload: value }), []);

  const {
    name = '',
    weight,
    length,
    width,
    height,
    serviceId,
    providerId,
    confirmationId,
    packageTypeId,
    domesticRateShopperGuid,
    intlServiceId,
    intlProviderId,
    intlConfirmationId,
    intlPackageTypeId,
    intlRateShopperGuid,
    noCustoms = false,
    customsDescription,
    customsValue,
    customsHarmonizationCode,
    customsCountry,
  } = state;

  const domesticData = {
    serviceId,
    providerId,
    confirmationId,
    onConfirmationIdChanged: onChangeConfirmationId,
    packageTypeId,
    onPackageTypeIdChanged: onChangePackageTypeId,
    rateShopperGuid: domesticRateShopperGuid,
  };

  const internationalData = {
    serviceId: intlServiceId,
    providerId: intlProviderId,
    confirmationId: intlConfirmationId,
    onConfirmationIdChanged: onChangeIntlConfirmationId,
    packageTypeId: intlPackageTypeId,
    onPackageTypeIdChanged: onChangeIntlPackageTypeId,
    rateShopperGuid: intlRateShopperGuid,
  };

  const onSave = useCallback(() => {
    if (!name) {
      return;
    }
    const sanitizedForm = {
      ...state,
      weightUnitOfMeasureId: weight ? sellerWeightUnit : WeightUnit.Unknown,
      dimensionUnitOfMeasureId: length || width || height ? sellerDimUnit : DimensionUnit.Unknown,
    };

    const updatePresetGroupPromise = newPresetGroup ? addPresetGroup(sanitizedForm) : updatePresetGroup(presetGroupData, sanitizedForm);
    updatePresetGroupPromise.promise.then((response) => {
      const updatedPresetGroupId = newPresetGroup ? response.data.productProfileId : presetGroupId;
      const entitiesToLoad = {
        entities: {
          productProfiles: {
            [updatedPresetGroupId]: {
              ...presetGroupData,
              productProfileId: updatedPresetGroupId,
              ...sanitizedForm,
            },
          },
        },
      };
      dispatch(entitiesLoad(entitiesToLoad));
      onClose();
      if (newPresetGroup) {
        setNeedsReload(!needsReload);
        if (!isProductPresetsSetupComplete) {
          updateSellerSettingsBulk({ [SellerSettings.isProductPresetsSetupComplete.key]: 'true' })
            .then((newSettings) => dispatch(saveNewSellerSettings(newSettings)))
            .catch((e) => LogService.logError(new Error('Failed to save product presets setup setting', { cause: e })));
        }
      }
    });
    return () => updatePresetGroupPromise?.cancel();
  }, [
    name,
    state,
    weight,
    sellerWeightUnit,
    length,
    width,
    height,
    sellerDimUnit,
    newPresetGroup,
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- presetGroupData changes every render
    presetGroupData,
    presetGroupId,
    dispatch,
    onClose,
    setNeedsReload,
    needsReload,
    isProductPresetsSetupComplete,
  ]);

  const headerFormattedMessage = newPresetGroup ? addModalHeader : editModalHeader;
  const [nameFieldIsDirty, setNameFieldIsDirty] = useState(false);
  const showNameFieldError = nameFieldIsDirty && !name;
  const onBlurCallback = useCallback(() => setNameFieldIsDirty(true), [setNameFieldIsDirty]);
  const presetGroupNameLabelId = 'add-preset-group-modal-preset-group-name';

  return (
    <Modal className={style['preset-group']} size='custom'>
      <ModalHeader closeModal={onClose}>{headerFormattedMessage}</ModalHeader>
      <ModalBody className={style['modal-body']}>
        <div className={style['header-description']}>
          <FormattedMessage {...messages.headerDescription} />
        </div>
        <div className={style['paired-options']}>
          <div className={style['leftside-label']} id={presetGroupNameLabelId}>
            <FormattedMessage {...messages.presetGroupNameLabel} />
          </div>
          <div className={style['paired-input']}>
            <Input
              aria-labelledby={presetGroupNameLabelId}
              className={style['input-wrapper']}
              hasError={showNameFieldError}
              name={presetGroupNameLabelId}
              onBlur={onBlurCallback}
              onChange={onChangeName}
              required
              value={name}
            />
          </div>
        </div>
        <div className={style['paired-options']}>
          <div className={style['leftside-label']}>
            <FormattedMessage {...messages.domesticServiceLabel} />
          </div>
          <div className={style['shipping-options']}>
            <PresetGroupsShippingServiceDropdowns existingData={domesticData} onChangeBulkHandler={onChangeBulkHandler} />
          </div>
        </div>
        <div className={style['paired-options']}>
          <div className={style['leftside-label']}>
            <FormattedMessage {...messages.internationalServiceLabel} />
          </div>
          <div className={style['shipping-options']}>
            <PresetGroupsShippingServiceDropdowns
              existingData={internationalData}
              isInternational
              onChangeBulkHandler={onChangeBulkHandler}
            />
          </div>
        </div>
        <div className={style['section-label']}>
          <FormattedMessage {...messages.shippingOptionsHeader} />
        </div>
        <div className={style['section-description']}>
          <FormattedMessage {...messages.shippingOptionsHeaderDescription} />
        </div>
        <div className={style['paired-options']}>
          <div className={style['leftside-label']}>
            <FormattedMessage {...messages.presetGroupWeight} />
          </div>
          <div className={style['paired-input']}>
            <div className={style['weight-wrapper']}>
              <WeightInput inputClassName={style['weight-input']} onChange={onChangeWeight} unit={sellerWeightUnit} weight={weight || 0} />
            </div>
          </div>
        </div>
        <div className={style['paired-options']}>
          <div className={style['leftside-label']}>
            <FormattedMessage {...messages.presetGroupDimensions} />
          </div>
          <div className={style['paired-input']}>
            <div className={style['size-wrapper']}>
              <DimensionInput
                dimensions={{
                  length: length ?? 0,
                  width: width ?? 0,
                  height: height ?? 0,
                }}
                inputClassName={style['size-input']}
                onChange={onChangeBulkHandler}
                unit={sellerDimUnit}
              />
            </div>
          </div>
        </div>
        <div className={style['section-label']}>
          <FormattedMessage {...messages.customsDeclarationsCheckbox} />
        </div>
        <div className={style['section-description']}>
          <FormattedMessage {...messages.customsHeaderDescription} />
        </div>
        <div className={style['paired-options']}>
          <Checkbox
            checked={noCustoms}
            className={style['no-customs-options']}
            label={<FormattedMessage {...messages.noAutoCustoms} />}
            onChange={onChangeNoCustoms}
          />
        </div>
        <div className={style['paired-options']}>
          <div className={style['leftside-label']}>
            <FormattedMessage {...messages.description} />
          </div>
          <div className={style['paired-input']}>
            <Input
              className={style['input-wrapper']}
              data-testid='customs-description'
              onChange={onChangeCustomsDescription}
              value={customsDescription}
            />
          </div>
        </div>
        <div className={style['paired-options']}>
          <div className={style['leftside-label']}>
            <FormattedMessage {...messages.declaredValue} />
          </div>
          <div className={style['paired-input']}>
            <CurrencyInput
              className={style['currency-input']}
              currencyCode={currencyCode}
              data-testid='customs-declared-value'
              onChange={onChangeCustomsValue}
              value={customsValue}
            />
          </div>
        </div>
        <div className={style['paired-options']}>
          <div className={style['leftside-label']}>
            <FormattedMessage {...messages.customsHarmonization} />
          </div>
          <div className={style['paired-input']}>
            <Input
              className={style['input-wrapper']}
              data-testid='customs-harmonization'
              onChange={onChangeCustomsHarmonizationCode}
              value={customsHarmonizationCode}
            />
          </div>
        </div>
        <div className={style['paired-options']}>
          <div className={style['leftside-label']}>
            <FormattedMessage {...messages.customsOriginCountry} />
          </div>
          <div className={style['origin-country']}>
            <CountryDropdown
              onChange={onChangeCustomsCountry}
              pickerClassName={style['dropdown-picker']}
              selectedCountryCode={customsCountry}
            />
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button onClick={onClose} theme='tertiary'>
          {cancel}
        </Button>
        <Button disabled={!name} onClick={onSave} theme='primary'>
          {save}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

PresetGroupModal.propTypes = {
  presetGroupId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  newPresetGroup: PropTypes.bool,
  needsReload: PropTypes.bool,
  setNeedsReload: PropTypes.func,
};

export default AsModal(PresetGroupModal);
